import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import Button from './Button';
import { createPlayer } from '../firebase';
import PlayerModal from './PlayerModal';
import { useAuth } from '../contexts/AuthContext';
import './FloatingPlayerIndicator.css';

const FloatingPlayerIndicator = ({
  selectedPlayers,
  onRemovePlayer,
  onAddPlayer,
  allPlayers,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();

  if (!selectedPlayers || selectedPlayers.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const submitCreatePlayer = async ({ name, age, emoji }) => {
    setCreating(true);
    try {
      await createPlayer(user.uid, name, emoji, age);
      setShowCreateModal(false);
      // Refresh or notify parent to reload players
      window.location.reload();
    } catch (error) {
      console.error('Error creating player:', error);
      alert('Failed to create player. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div
        className={`floating-player-indicator ${
          isExpanded ? 'expanded' : 'collapsed'
        }`}
      >
        {isExpanded ? (
          <>
            <div className='player-indicator-header'>
              <span className='indicator-title'>Players</span>
              <button
                className='collapse-btn'
                onClick={handleToggle}
                aria-label='Collapse player list'
              >
                <X />
              </button>
            </div>
            <div className='player-indicator-list'>
              {selectedPlayers.map((player) => (
                <button
                  key={player.id}
                  className='player-indicator-btn'
                  onClick={() => onRemovePlayer && onRemovePlayer(player.id)}
                  aria-label={`Remove ${player.playerName}`}
                >
                  <span className='player-avatar'>
                    {player.avatar || '🐶'}{' '}
                  </span>
                  <div className='player-info'>
                    <span className='player-name'>{player.playerName}</span>
                    <span className='player-points-badge'>
                      {player.totalScore || 0}
                    </span>
                  </div>
                  {onRemovePlayer && <Trash2 className='action-icon' />}
                </button>
              ))}

              {/* Add Player Section */}
              {onAddPlayer &&
                allPlayers &&
                (() => {
                  const availablePlayers = allPlayers.filter(
                    (player) =>
                      !selectedPlayers.find((sp) => sp.id === player.id)
                  );

                  return availablePlayers.length > 0 ? (
                    <div className='add-player-section'>
                      <div className='add-player-header'>
                        <span className='add-player-title'>Add Player</span>
                      </div>
                      <div className='available-players'>
                        {availablePlayers.map((player) => (
                          <button
                            key={player.id}
                            className='player-indicator-btn'
                            onClick={() => onAddPlayer(player.id)}
                            aria-label={`Add ${player.playerName}`}
                          >
                            <span className='player-avatar'>
                              {player.avatar || '🐶'}{' '}
                            </span>
                            <div className='player-info'>
                              <span className='player-name'>
                                {player.playerName}
                              </span>
                              <span className='player-points-badge'>
                                {player.totalScore || 0}
                              </span>
                            </div>
                            <Plus className='action-icon' />
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

              {/* Create New Player Button */}
              <Button
                type='secondary'
                size='small'
                onClick={() => setShowCreateModal(true)}
                className='create-player-btn'
              >
                Add New Player
              </Button>
            </div>
          </>
        ) : (
          <div className='collapsed-avatars' onClick={handleToggle}>
            {selectedPlayers.slice(0, 4).map((player, index) => (
              <div
                key={player.id}
                className='collapsed-player'
                style={{
                  zIndex: selectedPlayers.length - index,
                  marginLeft: index > 0 ? '-8px' : '0',
                }}
              >
                <span className='collapsed-avatar'>
                  {player.avatar || '🐶'}
                </span>
                <span className='collapsed-points'>
                  {player.totalScore || 0}
                </span>
              </div>
            ))}
            {selectedPlayers.length > 4 && (
              <span className='more-players'>
                +{selectedPlayers.length - 4}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Create Player Modal */}
      {showCreateModal && (
        <PlayerModal
          isOpen={showCreateModal}
          mode='create'
          isSubmitting={creating}
          onClose={() => setShowCreateModal(false)}
          onSubmit={submitCreatePlayer}
        />
      )}
    </>
  );
};

export default FloatingPlayerIndicator;
